import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, HostListener, TemplateRef, ViewChild } from '@angular/core';
import { SharedService } from '../../../../core/services/store/shared-store/shared-storage.service';
import { FlowManagerService } from '../../services/flow-manager.service';
import { Flow, FlowMetaData } from '../../../../shared/models';
import { Subscription } from 'rxjs';
import { take, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { NgClass } from '@angular/common';

export interface FlowData {
  name: string;
  lastEdited: string;
  author: string;
}

export interface AiSummary {
  description: string;
  isEditable?: boolean;
}

@Component({
    selector: 'flow-summary-sidebar',
    templateUrl: './flow-summary-sidebar.component.html',
    styleUrls: ['./flow-summary-sidebar.component.scss'],
    imports: [NgClass]
})
export class FlowSummarySidebarComponent implements OnInit, OnDestroy, OnChanges {
  @Input() isVisible = false;
  @Output() closeSidebarEvent = new EventEmitter<void>();

  sidebarWidth = 520;
  private readonly MIN_WIDTH = 320;
  private readonly MAX_WIDTH = 800;
  private isResizing = false;
  private startX = 0;
  private startWidth = 0;

  flowData: FlowData = {
    name: '',
    lastEdited: '',
    author: '',
  };

  aiSummary: AiSummary = {
    description: '',
    isEditable: false
  };

  regenerateButtonActive = true;
  isLoadingSummary = false;
  isRegenerating = false;
  isSaving = false;
  isTypewriting = false;
  displayedText = '';

  private subscriptions: Subscription = new Subscription();

  @ViewChild('regenerateConfirmDialog', { static: true }) regenerateConfirmDialog: TemplateRef<any>;

  constructor(
    private sharedService: SharedService,
    private flowManagerService: FlowManagerService,
    public dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.loadFlowData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isVisible && changes.isVisible.currentValue === true) {
      this.loadFlowData();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private loadFlowData(): void {
    try {
      const flowName = this.getFlowName();

      this.flowData = {
        name: flowName,
        lastEdited: 'Loading...',
        author: 'Loading...',
      };

      this.fetchFlowMetadata();
      this.loadFlowSummary();
    } catch (error) {
      console.warn('Could not load flow data:', error);
    }
  }

  private loadFlowSummary(): void {
    const flowId = this.sharedService.getFlowRef;

    if (!flowId) {
      return;
    }

    this.isLoadingSummary = true;

    const summarySubscription = this.flowManagerService
      .getFlowSummary(flowId)
      .pipe(
        take(1),
        catchError(error => {
          this.regenerateButtonActive = false;
          console.warn('Could not load flow summary:', error);
          return of(null);
        })
      )
      .subscribe({
        next: (response: any) => {
          this.isLoadingSummary = false;

          if (response && response.description) {
            this.aiSummary.description = response.description;
            this.displayedText = response.description;
          } else {
            this.aiSummary.description = 'No flow summary has been generated yet. A flow summary will be generated when a flow is saved.';
            this.displayedText = this.aiSummary.description;
          }
        },
        error: (error) => {
          this.isLoadingSummary = false;
          console.warn('Error loading flow summary:', error);
        }
      });

    this.subscriptions.add(summarySubscription);
  }

  private fetchFlowMetadata(): void {
    const flowId = this.sharedService.getFlowRef;

    if (!flowId) {
      return;
    }

    const metadataSubscription = this.flowManagerService
      .getFlowMetaDataById(flowId)
      .pipe(take(1))
      .subscribe({
        next: (metadata: FlowMetaData) => {
          this.updateFlowDataFromMetadata(metadata);
        },
        error: (error) => {
          console.warn('Could not load flow metadata, using fallback data:', error);
        }
      });

    this.subscriptions.add(metadataSubscription);
  }

  private updateFlowDataFromMetadata(metadata: FlowMetaData): void {
    if (metadata.createdBy) {
      this.flowData.author = metadata.createdBy;
    } else if (metadata.author) {
      this.flowData.author = metadata.author;
    }

    if (metadata.lastModified) {
      this.flowData.lastEdited = this.formatDate(metadata.lastModified);
    } else if (metadata.lastEdited) {
      this.flowData.lastEdited = this.formatDate(metadata.lastEdited);
    } else if (metadata.createdDate) {
      this.flowData.lastEdited = this.formatDate(metadata.createdDate);
    } else {
      this.flowData.lastEdited = this.formatDate(new Date().toISOString());
    }

    if (metadata.flowName && metadata.flowName !== this.flowData.name) {
      this.flowData.name = metadata.flowName;
    }
  }

  private formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.warn('Could not format date:', dateString, error);
    }
  }

  private getFlowName(): string {
    try {
      const flowName = this.sharedService.getFlowName;

      if (flowName) {
        return flowName;
      }

      if (this.sharedService.flow && this.sharedService.flow.metaData && this.sharedService.flow.metaData.flowName) {
        return this.sharedService.flow.metaData.flowName;
      }

      return 'New Flow';
    } catch (error) {
      console.warn('Could not get flow name:', error);
      return 'Untitled Flow';
    }
  }

  closeSidebar(): void {
    this.closeSidebarEvent.emit();
  }

  modifySummary(): void {
    this.aiSummary.isEditable = !this.aiSummary.isEditable;
  }

  private transformStepsToObject(flow: any): any {
    if (!flow.steps || !Array.isArray(flow.steps)) {
      return flow;
    }

    const transformedFlow = {
      ...flow,
      meta_data: flow.metaData,
      steps: {}
    };
    delete transformedFlow.metaData;

    flow.steps.forEach(step => {
      const { id, ...stepData } = step;
      transformedFlow.steps[id] = stepData;
    });

    return transformedFlow;
  }

  regenerateSummary(): void {
    const dialogRef = this.dialog.open(this.regenerateConfirmDialog, {
      panelClass: 'resetFlowDialog',
      width: '530px',
      height: '160px',
      disableClose: true,
    });
  }

  confirmRegenerate(): void {
    const flowId = this.sharedService.getFlowRef;
    const flow = this.sharedService.flow;

    if (!flowId || !flow) {
      console.warn('Cannot regenerate summary: missing flow ID or flow data');
      return;
    }

    this.isRegenerating = true;
    this.dialog.closeAll();

    const transformedFlow = this.transformStepsToObject(flow);

    const regenerateSubscription = this.flowManagerService
      .generateFlowDescription(transformedFlow)
      .pipe(
        take(1),
        catchError(error => {
          console.error('Error regenerating summary:', error);
          return of(null);
        })
      )
      .subscribe({
        next: (summary: any) => {
          if (summary && summary.contentSummary) {
            this.typewriterEffect(summary.contentSummary);
          } else {
            this.isRegenerating = false;
          }
        },
        error: (error) => {
          this.isRegenerating = false;
          console.error('Failed to regenerate summary:', error);
        }
      });

    this.subscriptions.add(regenerateSubscription);
  }

  cancelRegenerate(): void {
    this.dialog.closeAll();
  }

  private typewriterEffect(newText: string): void {
    this.isTypewriting = true;
    this.displayedText = '';
    let index = 0;

    const typeInterval = setInterval(() => {
      if (index < newText.length) {
        this.displayedText += newText.charAt(index);
        index++;
      } else {
        clearInterval(typeInterval);
        this.isTypewriting = false;
        this.isRegenerating = false;
        this.aiSummary.description = newText;
      }
    }, 5);
  }

  onSummaryEdit(event: Event): void {
    if (this.aiSummary.isEditable) {
      const target = event.target as HTMLElement;
      this.aiSummary.description = target.innerText;

      const flowId = this.sharedService.getFlowRef;

      if (!flowId || this.isSaving) {
        return;
      }

      this.isSaving = true;

      const saveSubscription = this.flowManagerService
        .createOrUpdateFlowSummary(flowId, this.aiSummary.description)
        .pipe(
          take(1),
          catchError(error => {
            console.error('Error saving summary edit:', error);
            return of(null);
          })
        )
        .subscribe({
          next: (response: any) => {
            this.isSaving = false;
          },
          error: (error) => {
            this.isSaving = false;
            console.error('Failed to save summary edit:', error);
          }
        });

      this.subscriptions.add(saveSubscription);
    }
  }

  startResize(event: MouseEvent | TouchEvent): void {
    event.preventDefault();
    this.isResizing = true;

    if (event instanceof MouseEvent) {
      this.startX = event.clientX;
    } else {
      this.startX = event.touches[0].clientX;
    }

    this.startWidth = this.sidebarWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }

  @HostListener('document:mousemove', ['$event'])
  @HostListener('document:touchmove', ['$event'])
  onResize(event: MouseEvent | TouchEvent): void {
    if (!this.isResizing) { return; }

    event.preventDefault();

    let clientX: number;
    if (event instanceof MouseEvent) {
      clientX = event.clientX;
    } else {
      clientX = event.touches[0].clientX;
    }

    const deltaX = this.startX - clientX;
    const newWidth = this.startWidth + deltaX;

    this.sidebarWidth = Math.max(this.MIN_WIDTH, Math.min(this.MAX_WIDTH, newWidth));
  }

  @HostListener('document:mouseup')
  @HostListener('document:touchend')
  stopResize(): void {
    if (this.isResizing) {
      this.isResizing = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
  }
}
